/**
 * @file ListUsersForRole.js
 * @description Displays users associated with a specific security role and allows user management.
 */

import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    fetchUsersForRole,
    deleteUserFromRole,
    fetchSecurityRoleById,
} from '../../services/securityRoleService';
import Toast from '../ui/toasts/Toast';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import Spinner from '../ui/spinners/Spinner';
import styles from './security-roles.module.scss';
import { useAuthContext } from '../../store/Contexts';
import { hasAddEditPermission } from './SecurityRolePermission';
import { LogEventType } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { ROUTES, ROUTE_BUILDERS } from '../../constants/routes';
import { getText } from '../../i18n';
const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const ListUsersForRole = () => {
    const { showModal } = useContext(ModalContext);
    const { roleId } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const { user, multitenantFlag, hasSecurityAttribute } = useAuthContext();

    const [sortConfig, setSortConfig] = useState({
        key: 'lastName',
        direction: SortDirections.ASCENDING,
    });

    const [roleDetails, setRoleDetails] = useState({
        name: '',
        description: '',
        attributes: [],
    });

    const selectAllRef = useRef(null);

    useEffect(() => {
        const loadRoleDetails = async () => {
            try {
                if (roleId) {
                    const fetchedRole = await fetchSecurityRoleById(
                        parseInt(roleId, 10),
                    );
                    setRoleDetails(fetchedRole);

                    const fetchedUsers = await fetchUsersForRole(roleId);
                    setUsers(fetchedUsers);
                }
            } catch (error) {
                console.error('Failed to load role Data', error);
                Toast.error(getText('msg_e7a7d6c6cbfa'));
            } finally {
                setIsLoading(false);
            }
        };

        loadRoleDetails();
    }, [roleId, navigate]);

    useEffect(() => {
        selectAllRef.current.indeterminate =
            selectedUsers.length > 0 && selectedUsers.length < users.length;
    }, [selectedUsers, users]);

    const handleSort = key => {
        setSortConfig(prevConfig => ({
            key,
            direction:
                prevConfig.key === key &&
                prevConfig.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING,
        }));
    };

    const sortedUsers = [...users].sort((a, b) => {
        if (!sortConfig.key) return 0;
        if (a[sortConfig.key] < b[sortConfig.key])
            return sortConfig.direction === SortDirections.ASCENDING ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key])
            return sortConfig.direction === SortDirections.ASCENDING ? 1 : -1;
        return 0;
    });

    const handleUserSelection = userId => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId],
        );
    };

    const handleSelectAllChange = isChecked => {
        if (isChecked) {
            setSelectedUsers(sortedUsers.map(user => user.userId));
        } else {
            setSelectedUsers([]);
        }
    };

    const openHandleDeleteSelectedUsersModal = () => {
        if (!selectedUsers.length) {
            Toast.error(getText('msg_d6844db7b26b'));
            return;
        }

        showModal({
            type: ModalTypes.DELETE_CONFIRMATION,
            title: getText('msg_0f4209e73a43'),
            onYes: () => handleDeleteSelectedUsers(),
            onNo: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_f6fdbe48dc54'),
        });
        logEvent(
            LogEventType.DeleteClicked,
            `Security role users delete clicked`,
        );
    };

    const handleDeleteSelectedUsers = async () => {
        await deleteUserFromRole(roleId, selectedUsers);
        Toast.success(
            `${
                selectedUsers.length >= 2 ? 'The Users were' : 'The User was'
            } deleted.`,
        );
        setUsers(users.filter(user => !selectedUsers.includes(user.userId)));
        setSelectedUsers([]);
    };

    const navigateToAddUser = () => {
        navigate(ROUTE_BUILDERS.securityRolesAddUser(roleId));
    };

    const handleClose = () => {
        navigate(ROUTES.SECURITY_ROLES);
    };

    const renderSortArrow = key => {
        if (sortConfig.key !== key) {
            return null;
        }
        return (
            <span className={`px-2 ${styles.sortArrowIcon}`}>
                <svg
                    width="11"
                    height="6"
                    viewBox="0 0 11 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d={
                            sortConfig.direction === SortDirections.ASCENDING
                                ? 'M1 5.25L5.25 0.75L9.5 5.25'
                                : 'M1 0.75L5.25 5.25L9.5 0.75'
                        }
                        stroke="#2F2B43"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        );
    };

    return (
        <div className={`page-content`}>
            <div className="card">
                <div className="card-body px-0 py-4 b">
                    <div className="d-flex justify-content-between align-items-center pb-3 border-bottom">
                        <p className="page-title float-start card-title ms-4 mb-0">
                            {`Users in Role: 
              ${isLoading ? '...' : roleDetails.name}
              `}
                        </p>

                        <div className="float-end d-flex align-items-center">
                            {hasAddEditPermission(
                                multitenantFlag,
                                hasSecurityAttribute,
                            ) && (
                                <span
                                    type="button"
                                    onClick={openHandleDeleteSelectedUsersModal}
                                    className="ing-text-buttons mx-3"
                                >
                                    <i className="bi bi-trash3 me-2" />
                                    {getText('msg_f6fdbe48dc54')}
                                </span>
                            )}
                            <span
                                type="button"
                                onClick={handleClose}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </span>
                            {hasAddEditPermission(
                                multitenantFlag,
                                hasSecurityAttribute,
                            ) && (
                                <div to="new-portal-page">
                                    <span
                                        onClick={navigateToAddUser}
                                        type="button"
                                        className="ms-3 me-4  button-base ing-button-primary-left align-middle"
                                    >
                                        <i className="bi bi-plus-lg me-1" />
                                        {getText('msg_61cc55aa0453')}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="table-responsive-xxl w-100">
                        <table className="table table-hover align-middle ing-table-striped">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    <th
                                        className={`text-center checkbox-column-head`}
                                    >
                                        <input
                                            ref={selectAllRef}
                                            type="checkbox"
                                            className={`form-check-input`}
                                            checked={
                                                selectedUsers.length ===
                                                    users.length &&
                                                users.length >= 1
                                            }
                                            onChange={e =>
                                                handleSelectAllChange(
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                    </th>
                                    <th
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleSort('lastName')}
                                    >
                                        {getText('msg_863cb39fbe7d')}
                                        {renderSortArrow('lastName')}
                                    </th>
                                    <th
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleSort('firstName')}
                                    >
                                        {getText('msg_b6ea992aab46')}
                                        {renderSortArrow('firstName')}
                                    </th>

                                    {user.jwt?.ind != null ? (
                                        <>
                                            <th
                                                style={{ cursor: 'pointer' }}
                                                onClick={() =>
                                                    handleSort('uniqueId')
                                                }
                                            >
                                                {getText('msg_7aba334bb9bf')}
                                                {renderSortArrow('uniqueId')}
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer' }}
                                                onClick={() =>
                                                    handleSort('department')
                                                }
                                            >
                                                {getText('msg_db40106a4051')}
                                                {renderSortArrow('department')}
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer' }}
                                                onClick={() =>
                                                    handleSort('location')
                                                }
                                            >
                                                {getText('msg_d219c68101f5')}
                                                {renderSortArrow('location')}
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer' }}
                                                onClick={() =>
                                                    handleSort('region')
                                                }
                                            >
                                                {getText('msg_0f217179940c')}
                                                {renderSortArrow('region')}
                                            </th>
                                        </>
                                    ) : (
                                        <>
                                            <th
                                                style={{ cursor: 'pointer' }}
                                                onClick={() =>
                                                    handleSort('emailAddress')
                                                }
                                            >
                                                {getText('msg_84add5b29527')}
                                                {renderSortArrow(
                                                    'emailAddress',
                                                )}
                                            </th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {!isLoading &&
                                    sortedUsers.map(user => (
                                        <tr key={user.userId}>
                                            <td className="text-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selectedUsers.includes(
                                                        user.userId,
                                                    )}
                                                    onChange={() =>
                                                        handleUserSelection(
                                                            user.userId,
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td>{user.lastName}</td>
                                            <td>{user.firstName}</td>

                                            {user.jwt?.ind != null ? (
                                                <>
                                                    <td>{user.uniqueId}</td>
                                                    <td>{user.department}</td>
                                                    <td>{user.location}</td>
                                                    <td>{user.region}</td>
                                                </>
                                            ) : (
                                                <>
                                                    <td>{user.emailAddress}</td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                            </tbody>
                        </table>

                        {isLoading ? (
                            <Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        ) : (
                            <>
                                {users.length === 0 && (
                                    <p className="text-center my-4">
                                        {getText('msg_babdb51e6378')}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ListUsersForRole;
